import { useCallback, useMemo, useState } from "react";
import { importConfigsDialogContext } from "./import-configs-dialog-context";
import { TabsValue } from "../../components/organisms/ImportConfigsDialog/ImportConfigsDialog";
import { Tables } from "@/lib/supabase/supabase_types_override";
import { useValidateCrmProjects } from "../../hooks/lib/useValidateCrmProjects";
import { CrmProjectImportFileObject, ImportConfigsDialogConfigurationParametersSchemaType } from "../../components/organisms/ImportConfigsDialog/types";
import { DialogRendererProps } from "@/lib/dynamic-dialog/types";

interface ImportConfigsDialogProviderProps {
    children: React.ReactNode;
    crm: Tables<'crm'> | null;
    dialogProps: DialogRendererProps<unknown>
}

export function ImportConfigsDialogProvider({ children, crm, dialogProps }: Readonly<ImportConfigsDialogProviderProps>) {
    const [currentTab, setCurrentTab] = useState<TabsValue>('import');
    const [rawFileData, setRawFileData] = useState<Record<string, string>[] | null>(null);
    const [editedConfigId, setEditedConfigId] = useState<string | null>(null);
    const { validatedProjects, validateProjects: _validateProjects, isLoading: isLoadingValidateProjects } = useValidateCrmProjects(crm?.id);
    const [fileObjectKeyToPropertyMap, setFileObjectKeyToPropertyMap] = useState<ImportConfigsDialogConfigurationParametersSchemaType | undefined>(undefined);
    const [detailsCrmConfigId, setDetailsCrmConfigId] = useState<string | null>(null);
    const fileObjectKeys = useMemo(() => rawFileData && rawFileData.length > 0 ? Object.keys(rawFileData[0]) : [], [rawFileData]);
    const fileObjectExampleValues = useMemo(() => rawFileData && rawFileData.length > 0 ? Object.values(rawFileData[0]) : [], [rawFileData]);
    const validateProjects = useCallback(() => {
        _validateProjects(
            parseIntoCrmProjectsWithConfigs(
                crm!,
                fileObjectKeyToPropertyMap!,
                rawFileData!
            )
        )
    }, [_validateProjects, crm, fileObjectKeyToPropertyMap, rawFileData])
    const editedCrmProjectId = editedConfigId?.split(".")[0];
    const editedCrmConfigId = editedConfigId?.split(".")[1];
    const isEditedConfigCrmProject = editedCrmConfigId === undefined;
    const editedConfig = isEditedConfigCrmProject
        ? validatedProjects?.find(p => p.id === editedCrmProjectId)
        : validatedProjects?.find(p => p.id === editedCrmProjectId)?.crm_config.find(c => c.id === editedCrmConfigId);
    const contextValue = useMemo(() => ({
        rawFileData,
        currentTab,
        setRawFileData,
        dialogProps,
        crm,
        editedConfigId,
        setEditedConfigId,
        setCurrentTab,
        validatedProjects,
        validateProjects,
        isLoadingValidateProjects,
        fileObjectKeyToPropertyMap,
        setFileObjectKeyToPropertyMap,
        detailsCrmConfigId,
        setDetailsCrmConfigId,
        fileObjectKeys,
        fileObjectExampleValues,
        isEditedConfigCrmProject,
        editedConfig
    }), [rawFileData, currentTab, dialogProps, crm, editedConfigId, validatedProjects, validateProjects, isLoadingValidateProjects, fileObjectKeyToPropertyMap, detailsCrmConfigId, fileObjectKeys, fileObjectExampleValues, isEditedConfigCrmProject, editedConfig]);

    return (
        <importConfigsDialogContext.Provider value={contextValue}>
            {children}
        </importConfigsDialogContext.Provider>
    );
}

const parseIntoCrmProjectsWithConfigs = (
    crm: Tables<'crm'>,
    parametersMapping: ImportConfigsDialogConfigurationParametersSchemaType,
    rawFileData: Record<string, string>[]
): CrmProjectImportFileObject[] => {
    const prepareSelectedProjectConfiguration = (dataRow: Record<string, string>) => {
        const projectParameters = crm.configuration?.parameter?.project || [];
        const selectedConfigurationParameters = [];
        for (const param of projectParameters) {
            const filePropertyKey = parametersMapping.crmProjectFilePropertyToParameterMap ? parametersMapping.crmProjectFilePropertyToParameterMap[param.id] : undefined;
            if (filePropertyKey) {
                selectedConfigurationParameters.push({
                    id: param.id,
                    value: dataRow[filePropertyKey] || ''
                });
            }
        }
        return selectedConfigurationParameters;
    }
    const prepareSelectedSurveyConfiguration = (dataRow: Record<string, string>) => {
        const surveyParameters = crm.configuration?.parameter?.survey || [];
        const selectedConfigurationParameters = [];
        for (const param of surveyParameters) {
            const filePropertyKey = parametersMapping.crmConfigFilePropertyToParameterMap ? parametersMapping.crmConfigFilePropertyToParameterMap[param.id] : undefined;
            if (filePropertyKey) {
                selectedConfigurationParameters.push({
                    id: param.id,
                    value: dataRow[filePropertyKey] || ''
                });
            }
        }
        return selectedConfigurationParameters;
    }

    // Group rows by project name
    const projectGroups = new Map<string, Record<string, string>[]>();

    for (const row of rawFileData) {
        const projectName = row[parametersMapping.crmProjectName];
        if (!projectGroups.has(projectName)) {
            projectGroups.set(projectName, []);
        }
        projectGroups.get(projectName)!.push(row);
    }

    // Create one project per unique name, with configs from all matching rows
    const crmProjectsWithConfigs: CrmProjectImportFileObject[] = Array.from(projectGroups.entries()).map(
        ([projectName, rows], projectIndex) => ({
            id: `${projectIndex}`,
            name: projectName,
            selected_configuration: {
                parameter: prepareSelectedProjectConfiguration(rows[0]) // Use first row for project params
            },
            crm_config: rows.map((row, configIndex) => ({
                id: `${projectIndex}.${configIndex}`,
                name: row[parametersMapping.crmConfigName],
                selected_configuration: {
                    parameter: prepareSelectedSurveyConfiguration(row)
                }
            }))
        } satisfies CrmProjectImportFileObject)
    );
    console.log(crmProjectsWithConfigs)
    return crmProjectsWithConfigs;
}